import { useCallback, useState } from 'react'

import { ConfirmInfoStage, type ConfirmInfoState, type ContractorReview } from '@/models/confirmInfo'
import { checksForCard, FillCard, FillChecksContext } from '@/models/fillApplication'
import type { Org } from '@/models/user'
import { CoverageCard } from '../App/Customers/FillApplication/CoverageCard'
import { EquipmentCard } from '../App/Customers/FillApplication/EquipmentCard'
import { FillJumpProvider } from '../App/Customers/FillApplication/FillJumpProvider'
import { UnsavedEditsDialog } from '../App/Customers/FillApplication/UnsavedEditsDialog'
import { useCoverageCardForm } from '../App/Customers/FillApplication/useCoverageCardForm'
import { useUnsavedEditsGuard } from '../App/Customers/FillApplication/useUnsavedEditsGuard'
import { ConfirmationStep } from './ConfirmationStep'
import { ContractorAnswersSection } from './ContractorAnswersSection'
import { CoverageSummary } from './CoverageSummary'
import { EquipmentSummary } from './EquipmentSummary'
import { MissingSummary } from './MissingSummary'
import { ReviewSection } from './ReviewSection'
import { useRosterEdits } from './useRosterEdits'

interface ContractorConfirmFormProps {
  orgId: string
  organisation: Org
  state: ConfirmInfoState
  contractor: ContractorReview
}

/** A contractor's review: the chat's answers by part, the coverages, the vehicles and drivers with auto, then Confirmation; each part edits in a dialog. */
export function ContractorConfirmForm({
  orgId,
  organisation,
  state,
  contractor,
}: ContractorConfirmFormProps) {
  const application = organisation.currentInsuranceApplication
  const coverage = useCoverageCardForm(orgId, application, contractor.coverageMenu)
  const [openCard, setOpenCard] = useState<FillCard | null>(null)
  // The answers' parts aren't Intake cards, so they open by title.
  const [openSection, setOpenSection] = useState<string | null>(null)
  const rosters = useRosterEdits(orgId, openCard === FillCard.EQUIPMENT)
  const checks = state.checks
  // Leaving the page (Back, a link, a reload) with an edit open warns first, as the trucking review does.
  const [dirtySections, setDirtySections] = useState<string[]>([])
  const markSection = useCallback((title: string, dirty: boolean) => {
    // The same array when nothing changed, so a re-render's report doesn't render again.
    setDirtySections((current) => {
      if (dirty === current.includes(title)) return current
      return dirty ? [...current, title] : current.filter((section) => section !== title)
    })
  }, [])
  const dirtyLabels = [...dirtySections, ...(coverage.isDirty ? ['Coverage'] : [])]
  const blocker = useUnsavedEditsGuard(dirtyLabels)

  return (
    <FillJumpProvider onShowCard={setOpenCard}>
      <FillChecksContext.Provider value={checks}>
        <div className="flex flex-col gap-4">
          {state.stage === ConfirmInfoStage.REVIEW && <MissingSummary checks={checks} />}
          {contractor.sections.map((section) => (
            <ContractorAnswersSection
              key={section.title}
              orgId={orgId}
              section={section}
              open={openSection === section.title}
              onOpen={() => setOpenSection(section.title)}
              onClose={() => setOpenSection(null)}
              onDirtyChange={(dirty) => markSection(section.title, dirty)}
            />
          ))}
          <ReviewSection
            title="Coverage"
            checks={checksForCard(checks, FillCard.COVERAGE)}
            open={openCard === FillCard.COVERAGE}
            onOpen={() => setOpenCard(FillCard.COVERAGE)}
            onClose={() => setOpenCard(null)}
            form={coverage}
            summary={<CoverageSummary application={application} />}
          >
            <CoverageCard checks={checks} form={coverage} plain menu={contractor.coverageMenu} />
          </ReviewSection>
          {contractor.vehicles && (
            <ReviewSection
              title="Vehicles and drivers"
              checks={checksForCard(checks, FillCard.EQUIPMENT)}
              open={openCard === FillCard.EQUIPMENT}
              onOpen={() => setOpenCard(FillCard.EQUIPMENT)}
              onClose={() => setOpenCard(null)}
              form={rosters}
              summary={<EquipmentSummary orgId={orgId} contractor />}
            >
              <EquipmentCard
                orgId={orgId}
                checks={[]}
                eld={state.eld}
                owner={null}
                plain
                hideEldButton
                withoutTrailers
              />
            </ReviewSection>
          )}
          <div className="mt-4">
            <ConfirmationStep orgId={orgId} state={state} checks={checks} edited={[]} />
          </div>
        </div>
        <UnsavedEditsDialog blocker={blocker} labels={dirtyLabels} />
      </FillChecksContext.Provider>
    </FillJumpProvider>
  )
}
